import { useEffect, useRef, useState } from "react";
import { api, fmt, imgUrl, formatApiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { type: "model", title: "", description: "", location: "", price: 0, price_tiers: null, category: "", images: [], active: true };

export default function ListingsAdmin() {
  const [listings, setListings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [imgInput, setImgInput] = useState("");
  const fileRef = useRef(null);

  const refresh = () => api.get("/admin/listings").then((r) => setListings(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { refresh(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing, price: Number(editing.price) || 0 };
      if (editing.id) await api.put(`/admin/listings/${editing.id}`, payload);
      else await api.post("/admin/listings", payload);
      toast.success(editing.id ? "Listing updated." : "Listing created.");
      setEditing(null);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/listings/${id}`);
      toast.success("Listing deleted.");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      setEditing((prev) => ({ ...prev, images: [...prev.images, data.url] }));
      toast.success("Image uploaded.");
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };

  const TYPES = ["model", "car", "house"];

  return (
    <div data-testid="admin-listings">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl text-white">Listings</h1>
        <button data-testid="add-listing-btn" onClick={() => setEditing({ ...EMPTY })}
          className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase px-5 py-3 transition-colors">
          <Plus className="w-4 h-4" /> Add Listing
        </button>
      </div>
      <Tabs defaultValue="model">
        <TabsList className="bg-[#0A0A0A] border border-white/10 mb-6">
          {TYPES.map((t) => (
            <TabsTrigger key={t} data-testid={`listings-tab-${t}`} value={t}>
              {t === "model" ? "Models" : t === "car" ? "Cars" : "Houses"} ({listings.filter((l) => l.type === t).length})
            </TabsTrigger>
          ))}
        </TabsList>
        {TYPES.map((t) => (
          <TabsContent key={t} value={t}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {listings.filter((l) => l.type === t).map((l) => (
                <div key={l.id} data-testid={`admin-listing-${l.id}`} className="border border-white/10 bg-[#0A0A0A] overflow-hidden">
                  {l.images?.[0] && <img src={imgUrl(l.images[0])} alt={l.title} className="w-full h-40 object-cover" />}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-sm font-medium text-white">{l.title}</h3>
                      {!l.active && <span className="text-[10px] uppercase tracking-widest text-zinc-500">Hidden</span>}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">{l.location} {l.price > 0 ? `· ${fmt(l.price)}` : "· No price set"}</p>
                    <div className="flex gap-2 mt-4">
                      <button data-testid={`edit-listing-${l.id}`} onClick={() => setEditing({ ...l })}
                        className="flex items-center gap-1.5 text-xs text-zinc-300 border border-white/15 hover:border-brand px-3 py-2 transition-colors">
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                      <button data-testid={`delete-listing-${l.id}`} onClick={() => remove(l.id)}
                        className="flex items-center gap-1.5 text-xs text-red-400 border border-white/15 hover:border-red-400 px-3 py-2 transition-colors">
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {listings.filter((l) => l.type === t).length === 0 && (
                <p className="text-sm text-zinc-500 col-span-full">No {t} listings yet.</p>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent data-testid="listing-dialog" className="bg-[#0A0A0A] border-white/10 text-zinc-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">{editing?.id ? "Edit Listing" : "New Listing"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label className="text-xs text-zinc-400">Type</Label>
                <div className="flex gap-2 mt-1">
                  {TYPES.map((t) => (
                    <button type="button" key={t} data-testid={`listing-type-${t}`}
                      onClick={() => setEditing({ ...editing, type: t })}
                      className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${editing.type === t ? "bg-brand border-brand text-white" : "border-white/15 text-zinc-400"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Title *</Label>
                <Input data-testid="listing-title" required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Description</Label>
                <Textarea data-testid="listing-description" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-zinc-400">Location</Label>
                  <Input data-testid="listing-location" value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Price (USD){editing.type === "car" ? "/hr" : ""}</Label>
                  <Input data-testid="listing-price" type="number" min="0" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
                </div>
              </div>
              {editing.type === "car" && (
                <div>
                  <Label className="text-xs text-zinc-400">Category</Label>
                  <Select value={editing.category || "none"} onValueChange={(v) => setEditing({ ...editing, category: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="listing-category" className="bg-white/5 border-white/10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      <SelectItem value="none">—</SelectItem>
                      <SelectItem value="coupe">Coupe</SelectItem>
                      <SelectItem value="convertible">Convertible</SelectItem>
                      <SelectItem value="suv">SUV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {editing.type === "house" && (
                <div>
                  <Label className="text-xs text-zinc-400">Pricing by duration (USD)</Label>
                  <div className="grid grid-cols-3 gap-3 mt-1">
                    {["4", "6", "8"].map((h) => (
                      <div key={h}>
                        <span className="text-[10px] text-zinc-500 uppercase">{h} hrs</span>
                        <Input data-testid={`listing-tier-${h}`} type="number" min="0"
                          value={editing.price_tiers?.[h] || ""}
                          onChange={(e) => setEditing({ ...editing, price_tiers: { ...(editing.price_tiers || {}), [h]: Number(e.target.value) || 0 } })}
                          className="bg-white/5 border-white/10 mt-1" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs text-zinc-400">Images</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {editing.images.map((img, i) => (
                    <div key={i} className="relative">
                      <img src={imgUrl(img)} alt="" className="w-16 h-16 object-cover border border-white/10" />
                      <button type="button" onClick={() => setEditing({ ...editing, images: editing.images.filter((_, j) => j !== i) })}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 rounded-full p-0.5" aria-label="Remove image">
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input data-testid="listing-image-url" placeholder="Paste image URL" value={imgInput} onChange={(e) => setImgInput(e.target.value)} className="bg-white/5 border-white/10" />
                  <button type="button" data-testid="listing-add-image-url"
                    onClick={() => { if (imgInput.trim()) { setEditing({ ...editing, images: [...editing.images, imgInput.trim()] }); setImgInput(""); } }}
                    className="text-xs border border-white/15 hover:border-brand px-3 whitespace-nowrap transition-colors">Add URL</button>
                  <button type="button" data-testid="listing-upload-btn" onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-1.5 text-xs border border-white/15 hover:border-brand px-3 whitespace-nowrap transition-colors">
                    <Upload className="w-3 h-3" /> Upload
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="listing-file-input" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch data-testid="listing-active" checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <Label className="text-xs text-zinc-400">Visible on site</Label>
              </div>
              <button data-testid="listing-save-btn" type="submit"
                className="w-full bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase py-3 transition-colors">
                Save Listing
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
